import React, { useState, useEffect, useMemo } from 'react';
import CloseIcon from '@material-ui/icons/Close';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Switch,
  TextField,
  Typography,
} from '@material-ui/core';

import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styles from './styles';
import { CountryRegionSelector } from './components/CountryRegionSelector';
import TranslationsTable from './components/TranslationsTable';
import { RootStateOrAny, useSelector } from 'react-redux';
import RBIAccordion from '../RBIAccordion';
import NameField from './components/NameField';
import EnumerationsTable from './components/EnumerationsTable';
import useCollection from './hooks/useCollection';
import ITranslation from '../../types/ITranslation';
import HeaderDialog from '../ConfirmDialog/HeaderDialog';
import Select from './components/Select';
import IEnumeration from '../../types/IEnumeration';
import { useInvariantGeographies } from './hooks/useInvariantGeographies';
import { usePreloadedTranslations } from './hooks/usePreloadedTranslations';

export type Permission = {
  controlled_by_corporate: boolean;
  read: boolean;
  write: boolean;
  country_id?: number;
  region: string;
};

export type Geography = {
  type: string;
  text: string;
  path: string;
  metas: {
    countryId?: string | number;
    region?: string;
    id?: number;
  };
};

type Brand = {
  brandId: number;
  brandCode: string;
  brandName: string;
};
interface IAttributesDialogProps {
  open: boolean;
  onClose: () => void;
  ActionButton: React.ComponentType<any>;
  titleKey: string;
  subtitleKey: string;
  invariants?: Geography[];
  id?: number;
  name: string;
  hasTrainingSystemAccess: boolean;
  isGeneralAttribute: boolean;
  attributeCategory?: number;
  isToggle: boolean;
  translations: ITranslation[];
  permissions: Permission[];
  enumValues: IEnumeration[];
  brand: Brand;
  readOnly?: boolean;
  lockedFields?: Record<string, boolean>;
}

const AttributesDialog = ({
  open,
  onClose,
  ActionButton,
  titleKey,
  subtitleKey,
  readOnly = false,
  lockedFields = {},
  brand,
  ...attribute
}: IAttributesDialogProps) => {
  const classes = styles();

  const { userInfo } = useSelector(
    (state: RootStateOrAny) => state.UserReducer,
  );

  const { categories } = useSelector(
    (state: RootStateOrAny) => state.AttributesReducer,
  );
  const validCategories = useMemo(
    () => (categories || []).filter(c => c.type === 'ATT'),
    [categories],
  );

  const defaultControlledByCorporate = useMemo(
    () => (attribute?.permissions || []).some(p => !!p.controlled_by_corporate),
    [attribute],
  );

  const defaultFormValues = useMemo(
    () => ({
      name: attribute.name,
      hasTrainingSystemAccess: attribute.hasTrainingSystemAccess,
      isGeneralAttribute: attribute.isGeneralAttribute,
      isToggle: attribute.isToggle,
      attributeCategory: attribute.attributeCategory,
      controlledByCorporate: defaultControlledByCorporate,
    }),
    [
      attribute.name,
      attribute.hasTrainingSystemAccess,
      attribute.isGeneralAttribute,
      attribute.isToggle,
      attribute.attributeCategory,
      defaultControlledByCorporate,
    ],
  );

  const invariantGeographies = useInvariantGeographies(attribute.permissions);
  const hasGlobalInvariantGeography = useMemo(
    () =>
      invariantGeographies.some(
        g => (g?.metas?.region || '').toUpperCase() === 'GLOBAL',
      ),
    [invariantGeographies],
  );
  const preloadedTranslations = usePreloadedTranslations(
    attribute.translations,
  );

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    reset,
    formState: { errors },
    trigger,
  } = useForm({
    defaultValues: defaultFormValues,
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  useEffect(() => {
    console.log(`resetting form for attribute: `, defaultFormValues);
    reset(defaultFormValues);
  }, [defaultFormValues, reset]);

  const isToggle = watch('isToggle');
  const name = watch('name');
  const hasTrainingSystemAccess = watch('hasTrainingSystemAccess');
  const isGeneralAttribute = watch('isGeneralAttribute');
  const attributeCategory = watch('attributeCategory');
  const controlledByCorporate = watch('controlledByCorporate');

  const { t } = useTranslation();

  const checkCompleteEnums = (enums: IEnumeration[]) =>
    enums.every(e => !!e.name && !!e.name.trim());

  const checkCompleteTranslations = (translations: ITranslation[]) =>
    translations.every(t => t.language?.code && (t.text || '').trim());

  const checkTranslationLanguageUniqueness = (translations: ITranslation[]) => {
    const codes = translations.map(t => t.language.code);
    const uniqueCodes = Array.from(new Set(codes));
    return codes.length === uniqueCodes.length;
  };

  const checkEnumeratorUniqueness = (enums: IEnumeration[]) => {
    const names = enums.map(e => e.name.trim());
    const uniqueNames = Array.from(new Set(names));
    return names.length === uniqueNames.length;
  };

  const {
    addNewElement: addNewEnum,
    collection: enums,
    modifyElement: modifyEnums,
    setCollection: setEnums,
    removeElement: removeEnums,
  } = useCollection<IEnumeration>({ name: '' }, checkCompleteEnums);

  const {
    addNewElement: addNewTranslation,
    collection: translations,
    modifyElement: modifyTranslation,
    setCollection: setTranslations,
    removeElement: removeTranslations,
  } = useCollection<ITranslation>(
    { language: { code: '' }, text: '' },
    checkCompleteTranslations,
  );

  const [hasTranslations, setHasTranslations] = useState(false);
  const [mainUIVisibility, setMainUIVisibility] = useState(true);
  const [countries, setCountries] = useState<Geography[]>([]);

  useEffect(() => {
    const idKeyedTranslations = ensureKeys(preloadedTranslations || []);
    setTranslations(idKeyedTranslations);
    setHasTranslations(!!preloadedTranslations?.length);
  }, [preloadedTranslations, setTranslations]);

  useEffect(() => {
    const idKeyedEnums = ensureKeys(attribute.enumValues || []);
    setEnums(idKeyedEnums);
  }, [attribute.enumValues, setEnums]);

  useEffect(() => {
    setCountries(invariantGeographies);
  }, [invariantGeographies]);

  const nameInputChange = event => {
    const { value } = event.target;

    const cleanValue = value.replace(/[^A-Za-z0-9\-_\s?¿]/g, '').slice(0, 30);

    setValue('name', cleanValue);
  };

  const prepareData = data => ({
    ...data,
    id: attribute.id,
    name,
    brand: brand.brandCode,
    translations: translations.map(val => {
      return {
        languageId: val.language.id,
        languageCode: val.language.code,
        text: val.text,
        id: val.id,
      };
    }),
    user: userInfo.sAMAccountName,
    enumValues: isToggle ? undefined : enums,
    permissions: countries.map(val => {
      return {
        region: val.metas.region,
        country_id: val.metas.countryId,
        read: 1,
        write: 1,
        controlled_by_corporate: controlledByCorporate ? 1 : 0,
        id: val.metas.id,
      };
    }),
  });

  const isValidAttributeType =
    isToggle || (enums.length >= 1 && checkCompleteEnums(enums));
  const hasValidTranslations = checkCompleteTranslations(translations);
  const isValidAvailability = countries.length >= 1;
  const translationLanguagesAreUnique =
    checkTranslationLanguageUniqueness(translations);
  const enumeratorsAreUnique = checkEnumeratorUniqueness(enums);
  const isErrorFree = !Object.values(errors).length;
  const disabled = [
    isValidAttributeType,
    isValidAvailability,
    name,
    attributeCategory,
    hasValidTranslations,
    translationLanguagesAreUnique,
    enumeratorsAreUnique,
    isErrorFree,
  ].some(condition => !condition);

  const handleModalClose = (onClose: () => void, reason) => {
    if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') onClose();
    else return;
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={(event, reason) => {
          handleModalClose(onClose, reason);
        }}
        maxWidth="lg"
        fullWidth
        style={{
          visibility: mainUIVisibility ? 'visible' : 'hidden',
        }}
        disableEscapeKeyDown
        className={classes.dialog}
      >
        <DialogTitle>
          <HeaderDialog titleKey={titleKey} subtitleKey={subtitleKey} />
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={onClose}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent className={classes.root}>
          <form>
            <RBIAccordion
              id="attributeInformation-accordion"
              expanded
              title={
                <Typography className={classes.cardTitle}>
                  Attribute Information
                </Typography>
              }
            >
              <Box className={classes.attributeInformationRow}>
                <NameField
                  value={name}
                  register={register}
                  onChange={nameInputChange}
                  disabled={readOnly || !!lockedFields.name}
                  error={errors?.name}
                  trigger={trigger}
                />
                <TextField
                  variant="outlined"
                  name="brand"
                  className={classes.attributeField}
                  inputProps={{
                    'data-testid': 'brand-textField',
                  }}
                  disabled
                  value={brand.brandName}
                />
              </Box>
              <Box className={classes.attributeInformationRow}>
                <CountryRegionSelector
                  availability={countries}
                  invariants={invariantGeographies}
                  handleAvailabilityChanged={setCountries}
                  disabled={
                    readOnly ||
                    !!lockedFields.permissions ||
                    hasGlobalInvariantGeography
                  }
                />
                <Controller
                  name="attributeCategory"
                  id="attributeCategory"
                  defaultValue={''}
                  control={control}
                  disabled={readOnly || !!lockedFields.attributeCategory}
                  render={field => {
                    const disabled =
                      readOnly || !!lockedFields.attributeCategory;
                    const finalField = { ...field, disabled };
                    return (
                      <Select
                        label="Category"
                        field={finalField}
                        options={validCategories}
                      />
                    );
                  }}
                />
              </Box>
              <Box className={classes.attributeInformationRow}>
                <Box className={classes.roundedBox}>
                  <FormControlLabel
                    control={
                      <Switch
                        disabled={
                          readOnly || !!lockedFields.hasTrainingSystemAccess
                        }
                        defaultChecked={attribute.hasTrainingSystemAccess}
                        className={classes.switch}
                        inputRef={register}
                        ref={register}
                        inputProps={
                          {
                            'data-testid': 'hasTrainingSystemAccess-switch',
                          } as React.InputHTMLAttributes<HTMLInputElement>
                        }
                        name="hasTrainingSystemAccess"
                        id="hasTrainingSystemAccess"
                      />
                    }
                    label="Training System Access"
                    className={classes.switchWithLabel}
                    labelPlacement="start"
                  />
                </Box>
                <Box className={classes.roundedBox}>
                  <FormControlLabel
                    control={
                      <Switch
                        disabled={readOnly || !!lockedFields.isGeneralAttribute}
                        defaultChecked={attribute.isGeneralAttribute}
                        className={classes.switch}
                        inputRef={register}
                        ref={register}
                        name="isGeneralAttribute"
                        inputProps={
                          {
                            'data-testid': 'isGeneralAttribute-switch',
                          } as React.InputHTMLAttributes<HTMLInputElement>
                        }
                        id="isGeneralAttribute"
                      />
                    }
                    label="Logs & Operations Access"
                    className={classes.switchWithLabel}
                    labelPlacement="start"
                  />
                </Box>
              </Box>
              <Box className={classes.attributeInformationRow}>
                <Box className={classes.roundedBox}>
                  <FormControlLabel
                    control={
                      <Switch
                        disabled={
                          readOnly || !!lockedFields.controlledByCorporate
                        }
                        defaultChecked={defaultControlledByCorporate}
                        className={classes.switch}
                        name="controlledByCorporate"
                        id="controlledByCorporate"
                        ref={register}
                        inputRef={register}
                      />
                    }
                    label="Controlled by Corporate"
                    className={classes.switchWithLabel}
                    labelPlacement="start"
                  />
                </Box>
              </Box>
            </RBIAccordion>
            <RBIAccordion
              expanded={hasTranslations}
              id="translations-accordion"
              title={
                <Typography className={classes.cardTitle} variant="caption">
                  Translations
                  <Typography className={classes.cardSubtitle} variant="h6">
                    {' '}
                    (Optional)
                  </Typography>
                </Typography>
              }
            >
              <Box className={classes.controlWithTitle}>
                <Typography variant="caption" className={classes.subtext}>
                  Specify languages for the attribute and provide translations.
                </Typography>
              </Box>
              <TranslationsTable
                disabled={readOnly || !!lockedFields.translations}
                addNewTranslation={addNewTranslation}
                elements={translations}
                editTranslation={modifyTranslation}
                removeTranslation={removeTranslations}
                canAddNewTranslation={checkCompleteTranslations}
              />
            </RBIAccordion>
            <RBIAccordion
              title={
                <Typography className={classes.cardTitle}>
                  Attribute Type
                </Typography>
              }
              id="attributeType-accordion"
              expanded
            >
              <Box className={classes.controlWithTitle}>
                <Typography variant="caption" className={classes.subtext}>
                  Is this attribute a single type attribute?
                </Typography>

                <Box className={classes.roundedBox}>
                  <Controller
                    name="isToggle"
                    id="isToggle"
                    control={control}
                    render={({ onChange, value, ...param }) => {
                      return (
                        <FormControlLabel
                          control={
                            <Switch
                              disabled={readOnly || !!lockedFields.isToggle}
                              defaultChecked={attribute.isToggle}
                              color="secondary"
                              className={classes.switch}
                              checked={value}
                              value={value}
                              onChange={(_, data) => {
                                onChange(data);
                              }}
                              inputProps={
                                {
                                  'data-testid': 'isToggle-switch',
                                } as React.InputHTMLAttributes<HTMLInputElement>
                              }
                              {...param}
                            />
                          }
                          label="Single Attribute"
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                          }}
                          labelPlacement="start"
                        />
                      );
                    }}
                  />
                </Box>
              </Box>
              {!isToggle && (
                <EnumerationsTable
                  disabled={readOnly || !!lockedFields.enumValues}
                  elements={enums}
                  editEnumerations={modifyEnums}
                  addNewEnum={addNewEnum}
                  removeEnum={removeEnums}
                  canAddNewEnum={checkCompleteEnums}
                />
              )}
            </RBIAccordion>
          </form>
        </DialogContent>
        <DialogActions>
          <Box width="100%" display="flex" justifyContent="flex-end">
            <Button
              variant="outlined"
              className={classes.buttonStyle}
              onClick={onClose}
            >
              {t('attributes.button.cancel')}
            </Button>
            <ActionButton
              prepareData={prepareData}
              handleSubmit={handleSubmit}
              setContainingModalVisibility={setMainUIVisibility}
              disabled={disabled}
              attribute={{
                isGeneralAttribute,
                hasTrainingSystemAccess,
                countries,
                name,
                isToggle,
              }}
            />
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
};

function ensureKeys(array) {
  return array.map(el => ({ key: el.id, ...el }));
}

export default AttributesDialog;
